import { Button } from '@HoudiniUI/Button';
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogPopup, DialogTitle } from '@HoudiniUI/Dialog';
import { Skeleton } from '@HoudiniUI/Skeleton';
import {
	useTelephonyComplianceReviewOverrideMutation,
	useTelephonyComplianceReviewRunMutation,
	useTelephonyConfigurationQuery,
} from '@Queries/index';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useAdminUserSession } from '../../../../../models/hooks/adminStateHooks';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { AdminPermission } from '../../../../../viewmodels/AdminViewModels';
import { InternalOnly } from '../../../../components/InternalOnly';
import { FindingsSection } from './FindingsSection';
import { HistorySection } from './HistorySection';
import { RegistrationStatusPanel } from './RegistrationStatusPanel';
import { ReleaseHoldDialog } from './ReleaseHoldDialog';
import { VerdictBanner } from './VerdictBanner';
import {
	VERIFY_LABEL,
	areAllCampaignsAccepted,
	deriveCompliance,
	deriveVerdict,
	filterCampaignsForDisplay,
	formatDate,
} from './complianceDerived';
import { styleSheet } from './styles';

const TextingRegistrationContent = ({ account }: { account: Api.IAccount }) => {
	const impersonationContext = React.useMemo<Api.IImpersonationContext>(
		() => ({ account: { id: account.id } }),
		[account.id]
	);
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError, logEvent } = useEventLogging('TextingRegistration');
	const [showReleaseDialog, setShowReleaseDialog] = React.useState(false);
	const [showRetryDialog, setShowRetryDialog] = React.useState(false);
	const [showRunConfirmation, setShowRunConfirmation] = React.useState(false);

	const configQuery = useTelephonyConfigurationQuery({ impersonationContext });

	const runMutation = useTelephonyComplianceReviewRunMutation({
		onSuccess: () => {
			toast.success('New review recorded. Any previous release no longer applies.');
			setShowRunConfirmation(false);
		},
		onError: error => {
			logApiError('TelephonyComplianceReviewRun', error);
			errorMessages.pushApiError(error);
			setShowRunConfirmation(false);
		},
	});
	const overrideMutation = useTelephonyComplianceReviewOverrideMutation({
		onSuccess: () => {
			toast.success('Hold released and campaign submitted.');
			setShowReleaseDialog(false);
			setShowRetryDialog(false);
		},
		onError: error => {
			logApiError('TelephonyComplianceReviewOverride', error);
			errorMessages.pushApiError(error);
		},
	});

	if (configQuery.isPending) {
		return (
			<div className={css(styleSheet.container)}>
				<Skeleton styles={[styleSheet.skeletonBanner]} />
				<Skeleton styles={[styleSheet.skeletonBar]} />
			</div>
		);
	}
	if (configQuery.isError) {
		return <p className={css(styleSheet.emptyMessage)}>Registration details could not be loaded.</p>;
	}
	const config = configQuery.data;
	const registration = config?.campaignRegistration;
	if (!registration) {
		return <p className={css(styleSheet.emptyMessage)}>No texting registration was found for this account.</p>;
	}

	const allCampaigns = registration.campaigns ?? [];
	const campaigns = filterCampaignsForDisplay(allCampaigns);
	const review = registration.complianceReview;
	const compliance = deriveCompliance(review);
	const verdict = deriveVerdict(compliance, campaigns, allCampaigns);
	const allAccepted = areAllCampaignsAccepted(campaigns);
	const hasSupportLevel3 = userSession.hasPermission(AdminPermission.SupportLevel3);
	const holdActive = verdict?.kind === 'held' || verdict?.kind === 'releaseNotSubmitted';
	// Collapsed while there is something to fix, so the findings sit directly under the summary line.
	const statusDefaultOpen = allAccepted || compliance.blockers.length === 0;

	const startRun = () => {
		logEvent('ComplianceReviewRun-Start', { accountId: account.id });
		runMutation.mutate({ impersonationContext });
	};
	const onVerifyClick = () => {
		if (verdict?.runFriction === 'none') {
			startRun();
		} else {
			setShowRunConfirmation(true);
		}
	};

	// The retry dialog has no reason field and the server refuses an empty reason, so a release with
	// no stored reason routes to the release dialog to collect one.
	const storedReleaseReason = compliance.latestOverride?.reason?.trim();
	const onRetryClick = () => (storedReleaseReason ? setShowRetryDialog(true) : setShowReleaseDialog(true));

	return (
		<div className={css(styleSheet.container)}>
			{verdict ? (
				<VerdictBanner verdict={verdict} mode={review?.mode}>
					{hasSupportLevel3 ? (
						<>
							{verdict.showRetry ? <Button onClick={onRetryClick}>Retry the submission</Button> : null}
							<Button
								variant={
									verdict.verify === 'primary' ? 'primary' : verdict.verify === 'secondary' ? 'secondary' : 'ghost'
								}
								styles={verdict.verify === 'quiet' ? [styleSheet.quietAction] : []}
								isLoading={runMutation.isPending && !showRunConfirmation}
								onClick={onVerifyClick}
							>
								{verdict.verify === 'quiet' ? `${VERIFY_LABEL} (retires this release)` : VERIFY_LABEL}
							</Button>
							{verdict.showRelease ? (
								<Button variant='ghost' styles={[styleSheet.quietAction]} onClick={() => setShowReleaseDialog(true)}>
									Release the hold without fixing…
								</Button>
							) : null}
						</>
					) : null}
				</VerdictBanner>
			) : null}
			<RegistrationStatusPanel
				config={config}
				compliance={compliance}
				impersonationContext={impersonationContext}
				defaultOpen={statusDefaultOpen}
				holdActive={holdActive}
			/>
			{verdict?.kind !== 'neverRun' ? (
				<FindingsSection
					blockers={compliance.blockers}
					warnings={compliance.warnings}
					label={verdict?.findingsLabel ?? 'What the client needs to fix'}
					collapseBlockers={verdict?.collapseFindings ?? false}
				/>
			) : null}
			<HistorySection compliance={compliance} />
			<ReleaseHoldDialog
				open={showReleaseDialog}
				onOpenChange={setShowReleaseDialog}
				blockers={compliance.blockers}
				isPending={overrideMutation.isPending}
				onConfirm={reason => {
					logEvent('ComplianceReviewOverride-Start', { accountId: account.id });
					overrideMutation.mutate({ impersonationContext, reason });
				}}
			/>
			<Dialog open={showRetryDialog} onOpenChange={setShowRetryDialog}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Retry the submission</DialogTitle>
					</DialogHeader>
					<DialogBody styles={[styleSheet.dialogStack]}>
						<p className={css(styleSheet.dialogText)}>
							{compliance.latestOverride?.by ?? 'An admin'}’s release from {formatDate(compliance.latestOverride?.at)}{' '}
							is already on the record. This retries the submission under it and{' '}
							<strong>does not record a second release</strong>.
						</p>
						<p className={css(styleSheet.warnBox)}>
							The findings were never fixed, so the provider may decline this campaign for the same reasons.
						</p>
					</DialogBody>
					<DialogFooter>
						<Button variant='outline' onClick={() => setShowRetryDialog(false)}>
							Cancel
						</Button>
						<Button
							isLoading={overrideMutation.isPending}
							onClick={() => {
								logEvent('ComplianceReviewOverrideRetry-Start', { accountId: account.id });
								overrideMutation.mutate({ impersonationContext, reason: compliance.latestOverride?.reason ?? '' });
							}}
						>
							Retry submission
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
			<Dialog open={showRunConfirmation} onOpenChange={setShowRunConfirmation}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Check the pages again?</DialogTitle>
					</DialogHeader>
					<DialogBody styles={[styleSheet.dialogStack]}>
						{verdict?.runFriction === 'overridden' ? (
							<>
								<p className={css(styleSheet.warnBox)}>
									This registration’s hold is currently released by an override from{' '}
									<strong>{compliance.latestOverride?.by ?? 'an admin'}</strong> (
									{formatDate(compliance.latestOverride?.at)}).
								</p>
								<p className={css(styleSheet.dialogText)}>
									A new review <strong>retires that release</strong>. If it fails, the registration goes back behind the
									gate and needs releasing again.
								</p>
								<p className={css(styleSheet.dialogText)}>If it passes, no release is needed any more.</p>
							</>
						) : (
							<p className={css(styleSheet.dialogText)}>
								The current review passes. If a new one fails, this registration is held until it passes again or
								someone releases it.
							</p>
						)}
					</DialogBody>
					<DialogFooter>
						<Button variant='outline' onClick={() => setShowRunConfirmation(false)}>
							Cancel
						</Button>
						<Button
							variant={verdict?.runFriction === 'overridden' ? 'destructive' : 'primary'}
							isLoading={runMutation.isPending}
							onClick={startRun}
						>
							{verdict?.runFriction === 'overridden' ? 'Check and retire the release' : 'Check again'}
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</div>
	);
};

// Internal-only in full, every branch included: the block this replaces was guarded until the V2 page
// dropped it by accident.
export const TextingRegistration = ({ account }: { account: Api.IAccount }) => (
	<InternalOnly>
		<TextingRegistrationContent account={account} />
	</InternalOnly>
);
